/**
 * Component: sa-carousel-img-text
 * Description: Image Carousel with Title and Subtitle
 */
if (!customElements.get('sa-carousel-img-text')) {
  class SaCarouselImgText extends HTMLElement {
    constructor() {
      super();
      this.currentFiles = 0;
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.startAutoPlay = this.startAutoPlay.bind(this);
      this.stopAutoPlay = this.stopAutoPlay.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      
      // Touch handling
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.sa-carousel-img-text__track');
      this.slides = Array.from(this.querySelectorAll('.sa-carousel-img-text__slide'));
      this.dotsContainer = this.querySelector('.sa-carousel-img-text__dots');
      this.prevBtn = this.querySelector('.sa-carousel-img-text__nav-btn--prev');
      this.nextBtn = this.querySelector('.sa-carousel-img-text__nav-btn--next');
      
      if (!this.track || this.slides.length === 0) return;

      this.autoPlaySpeed = parseInt(this.getAttribute('data-autoplay-speed')) * 1000 || 3000;
      this.currentIndex = 0;
      this.totalSlides = this.slides.length;

      // Build pagination dots (mobile)
      if (this.dotsContainer) {
        this.dotsContainer.innerHTML = '';
        this.dots = this.slides.map((_, idx) => {
          const dot = document.createElement('div');
          dot.className = 'sa-carousel-img-text__dot';
          dot.setAttribute('data-index', String(idx));
          dot.setAttribute('aria-label', `Go to slide ${idx + 1}`);
          dot.addEventListener('click', this.handleDotClick);
          this.dotsContainer.appendChild(dot);
          return dot;
        });
      }

      // Event Listeners
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleNext);

      // Touch events for mobile swipe
      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.track.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      this.track.addEventListener('touchend', this.handleTouchEnd);
      
      // Pause on hover
      this.addEventListener('mouseenter', this.stopAutoPlay);
      this.addEventListener('mouseleave', this.startAutoPlay);

      // Init
      this.updateCarousel();
      this.startAutoPlay();
      
      // Handle resize to reset position
      this.resizeObserver = new ResizeObserver(() => {
         this.updateCarousel(false); // Update without animation
      });
      this.resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      this.stopAutoPlay();
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleNext);
      if (this.track) {
        this.track.removeEventListener('touchstart', this.handleTouchStart);
        this.track.removeEventListener('touchmove', this.handleTouchMove);
        this.track.removeEventListener('touchend', this.handleTouchEnd);
      }
      if (this.dots && this.dots.length) {
        this.dots.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
      }
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    startAutoPlay() {
      this.stopAutoPlay(); // Clear existing
      if (this.autoPlaySpeed > 0) {
        this.interval = setInterval(this.handleNext, this.autoPlaySpeed);
      }
    }

    stopAutoPlay() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    }

    handlePrev() {
      this.currentIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
      this.updateCarousel();
    }

    handleNext() {
      this.currentIndex = (this.currentIndex + 1) % this.totalSlides;
      this.updateCarousel();
    }
    
    // Touch Logic
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchEndX = this.touchStartX; // Reset end position to start position
      this.stopAutoPlay();
    }
    
    handleTouchMove(e) {
      this.touchEndX = e.touches[0].clientX;
    }
    
    handleTouchEnd() {
      const diff = this.touchStartX - this.touchEndX;
      const threshold = 50; // min swipe distance
      
      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.handleNext(); // Swipe Left -> Next
        } else {
          this.handlePrev(); // Swipe Right -> Prev
        }
      }
      this.startAutoPlay();
    }

    handleDotClick(e) {
      const indexAttr = e.currentTarget.getAttribute('data-index');
      const idx = parseInt(indexAttr || '0', 10);
      if (!Number.isNaN(idx)) {
        this.currentIndex = idx;
        this.updateCarousel();
      }
    }

    updateCarousel(animate = true) {
      this.track.style.transition = animate ? 'transform 0.5s ease-in-out' : 'none';
      const percentage = this.currentIndex * 100;
      this.track.style.transform = `translateX(-${percentage}%)`;

       // Update dots active state
      if (this.dots && this.dots.length) {
        this.dots.forEach((dot, idx) => {
          const isActive = idx === this.currentIndex;
          dot.classList.toggle('sa-carousel-img-text__dot--active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }
    }
  }

  customElements.define('sa-carousel-img-text', SaCarouselImgText);
}
